import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { fetchMenuDetails } from 'src/features/MenuBuilderSlice'
import styled from 'styled-components'
import {
  Card,
  Button,
  List,
  Popconfirm,
  Input,
  Empty,
  Tooltip,
  Modal,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import CreateMenuModal from './CreateMenuModal'
import EditMenuModal from './EditMenuModal'
import ShareWithGroupButton from './ShareWithGroupButton'
import MenuGpChips from './MenuGpChips'
import { menuBuilderMenusWhyCopy } from 'src/constants/menuBuilderCopy'
import { isRecipeArchived } from 'src/utils/recipeMonitoring'

const MenusContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const Title = styled.h1`
  font-size: 28px;
  margin: 0;
`

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`

const StyledList = styled(List)`
  .ant-list-item {
    padding: 16px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-list-item:last-child {
    border-bottom: none;
  }
`

const MenuItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const MenuInfo = styled.div`
  flex: 1;

  .menu-name {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 4px;
  }

  .menu-description {
    color: #666;
    margin-bottom: 8px;
  }

  .menu-stats {
    display: flex;
    gap: 16px;
    font-size: 12px;
    color: #999;
  }
`

const MenuActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const MenusTab = ({ menus, onMenuAction, venueRecipes = [], venueId }) => {
  const dispatch = useDispatch()
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingMenu, setEditingMenu] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [duplicateMenuName, setDuplicateMenuName] = useState('')

  const filteredMenus =
    menus?.filter(
      menu =>
        menu.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (menu.description &&
          menu.description.toLowerCase().includes(searchTerm.toLowerCase())),
    ) || []

  const handleCreateMenu = menuData => {
    onMenuAction('create', null, menuData)
    setShowCreateModal(false)
  }

  const handleEditMenu = (menuId, menuData) => {
    onMenuAction('update', menuId, menuData)
    setShowEditModal(false)
    setEditingMenu(null)
  }

  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [menuToDuplicate, setMenuToDuplicate] = useState(null)

  const handleDuplicateMenu = menu => {
    setMenuToDuplicate(menu)
    setDuplicateMenuName(`${menu.name} (Copy)`)
    setShowDuplicateModal(true)
  }

  const handleDuplicateConfirm = () => {
    if (!menuToDuplicate) return

    const menuName = duplicateMenuName || `${menuToDuplicate.name} (Copy)`
    if (menuName.trim().length < 2) {
      return
    }
    if (menuName.trim().length > 80) {
      return
    }

    onMenuAction('duplicate', menuToDuplicate._id, { name: menuName })
    setShowDuplicateModal(false)
    setMenuToDuplicate(null)
    setDuplicateMenuName('')
  }

  const handleDuplicateCancel = () => {
    setShowDuplicateModal(false)
    setMenuToDuplicate(null)
    setDuplicateMenuName('')
  }

  const handleDeleteMenu = menu => {
    Modal.confirm({
      title: 'Delete Menu',
      content: `Are you sure you want to permanently delete "${menu.name}"? This action cannot be undone.`,
      okText: 'Delete',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: () => onMenuAction('delete', menu._id),
    })
  }

  const handleDownloadMenu = async menu => {
    try {
      // Fetch the detailed menu data with sections and recipes
      const menuDetails = await dispatch(fetchMenuDetails(menu._id))

      if (menuDetails?.menu) {
        const csvData = generateMenuCSV(menuDetails.menu)

        // Create filename with current date
        const today = new Date().toISOString().split('T')[0]
        const filename = `${menu.name.replace(
          /[^a-zA-Z0-9]/g,
          '_',
        )}_menu_data_${today}.csv`

        // Create blob and download
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        throw new Error('No menu data received from server')
      }
    } catch (error) {
      console.error('Error downloading menu:', error)
      alert(`Failed to download menu data: ${error.message}. Please try again.`)
    }
  }

  // Helper function to generate CSV data with real menu data
  const generateMenuCSV = menu => {
    const csvRows = []

    // Add header row with your column names, including Section
    csvRows.push(
      [
        'Recipe Name',
        'Section',
        'Cost per portion',
        'Selling Price (ex VAT)',
        'Selling Price (inc VAT)',
        'Has VAT',
        'Margin Price',
        'GP%',
        'Category',
      ].join(','),
    )

    // Iterate through menu sections and their recipes
    if (menu.sections && Array.isArray(menu.sections)) {
      menu.sections.forEach(section => {
        if (section.recipes && Array.isArray(section.recipes)) {
          section.recipes.forEach(recipe => {
            const archived = isRecipeArchived(recipe)
            const costPerPortion = recipe.costPerPortion || 0
            const marginPrice = recipe.sellingPrice || 0 // This is the ex-VAT margin price
            const sellingPriceExVat = marginPrice
            const sellingPriceIncVat = recipe.hasVat
              ? marginPrice * 1.2
              : marginPrice
            const hasVat = recipe.hasVat ? 'True' : 'False'
            const category = recipe.category?.name || 'Uncategorized'

            // Calculate GP% (gross profit percentage) using margin price (ex-VAT)
            const gpPercent =
              !archived && marginPrice > 0
                ? ((marginPrice - costPerPortion) / marginPrice) * 100
                : 0

            csvRows.push(
              [
                `"${recipe.title || 'Unnamed Recipe'}"`,
                `"${section.name || 'Unnamed Section'}"`,
                archived ? '' : costPerPortion.toFixed(2),
                sellingPriceExVat.toFixed(2),
                sellingPriceIncVat.toFixed(2),
                hasVat,
                marginPrice.toFixed(2),
                archived ? '' : gpPercent.toFixed(1) + '%',
                `"${category}"`,
              ].join(','),
            )
          })
        }
      })
    }

    return csvRows.join('\n')
  }

  return (
    <MenusContainer>
      <Header>
        <div>
          <Title>Menus</Title>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '8px',
            }}
          >
            <span
              style={{ color: '#666', fontSize: '14px' }}
              data-testid='menus-why-subtitle'
            >
              {menuBuilderMenusWhyCopy.subtitle}
            </span>
            <Tooltip title={menuBuilderMenusWhyCopy.tooltip}>
              <InfoCircleOutlined
                style={{ color: '#1890ff' }}
                data-testid='menus-why-info'
              />
            </Tooltip>
          </div>
        </div>
        <HeaderActions>
          <Button type='primary' onClick={() => setShowCreateModal(true)}>
            <PlusOutlined /> Create Menu
          </Button>
        </HeaderActions>
      </Header>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder='Search menus by name or description'
            allowClear
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
          />
        </div>

        {filteredMenus.length > 0 ? (
          <StyledList
            dataSource={filteredMenus}
            renderItem={menu => (
              <List.Item>
                <MenuItem>
                  <MenuInfo>
                    <div className='menu-name'>{menu.name}</div>
                    <MenuGpChips menu={menu} />
                    {menu.description && (
                      <div className='menu-description'>{menu.description}</div>
                    )}
                    <div className='menu-stats'>
                      <span>{menu.recipeCount || 0} recipes</span>
                      <span>{menu.sectionCount || 0} sections</span>
                      <span>
                        Updated{' '}
                        {new Date(menu.lastUpdated).toLocaleDateString()}
                      </span>
                    </div>
                  </MenuInfo>
                  <MenuActions>
                    <ShareWithGroupButton
                      offer={{ kind: 'menu', venueId, menuId: menu._id }}
                    />
                    <Button
                      type='link'
                      onClick={() => handleDownloadMenu(menu)}
                    >
                      <DownloadOutlined /> Download
                    </Button>
                    <Button
                      type='link'
                      onClick={() => {
                        setEditingMenu(menu)
                        setShowEditModal(true)
                      }}
                    >
                      <EditOutlined /> Edit
                    </Button>
                    <Button
                      type='link'
                      onClick={() => handleDuplicateMenu(menu)}
                    >
                      <CopyOutlined /> Duplicate
                    </Button>
                    <Popconfirm
                      title='Are you sure to delete this menu?'
                      description='This action cannot be undone.'
                      onConfirm={() => handleDeleteMenu(menu)}
                      okText='Yes'
                      cancelText='No'
                    >
                      <Button type='link' danger>
                        <DeleteOutlined /> Delete
                      </Button>
                    </Popconfirm>
                  </MenuActions>
                </MenuItem>
              </List.Item>
            )}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span data-testid='menus-why-empty'>
                <div>{menuBuilderMenusWhyCopy.emptyTitle}</div>
                <div style={{ color: '#666', marginTop: 4 }}>
                  {menuBuilderMenusWhyCopy.emptyBody}
                </div>
              </span>
            }
          >
            <Button type='primary' onClick={() => setShowCreateModal(true)}>
              <PlusOutlined /> Create Your First Menu
            </Button>
          </Empty>
        )}
      </Card>

      <CreateMenuModal
        visible={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onOk={handleCreateMenu}
        maskClosable={false}
      />

      <EditMenuModal
        visible={showEditModal}
        menu={editingMenu}
        venueRecipes={venueRecipes}
        onCancel={() => {
          setShowEditModal(false)
          setEditingMenu(null)
        }}
        onOk={handleEditMenu}
      />

      <Modal
        title='Duplicate Menu'
        visible={showDuplicateModal}
        onOk={handleDuplicateConfirm}
        onCancel={handleDuplicateCancel}
        okText='Duplicate'
        cancelText='Cancel'
        maskClosable={false}
      >
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
            }}
          >
            * New Menu Name:
          </label>
          <Input
            placeholder='Enter menu name'
            value={duplicateMenuName}
            onChange={e => setDuplicateMenuName(e.target.value)}
            style={{ width: '100%' }}
            autoFocus
          />
        </div>
      </Modal>
    </MenusContainer>
  )
}

export default MenusTab
